/* Natua · interacciones de la landing (sin dependencias, carga diferida).
   Galería · selector de paquetes · oferta al comprar · barras fijas · fecha de entrega · plato interactivo · tachado · carrito. */
(() => {
  // Marca para la red de seguridad de layout/theme.liquid: si este archivo no corrió, 6 s después de cargar se sueltan las fotos en espera
  window.natuaReady = true;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Limpieza al quitar/recargar secciones en el editor de temas ---------- */
  const cleanups = new Map();
  const onCleanup = (element, fn) => {
    if (!cleanups.has(element)) cleanups.set(element, []);
    cleanups.get(element).push(fn);
  };
  document.addEventListener('shopify:section:unload', (event) => {
    cleanups.forEach((fns, element) => {
      if (!event.target.contains(element)) return;
      fns.forEach((fn) => fn());
      cleanups.delete(element);
    });
  });

  /* ---------- Galería con scroll-snap ---------- */
  function initGallery(root) {
    const track = root.querySelector('[data-n-gallery-track]');
    if (!track || track.dataset.nReady) return;
    track.dataset.nReady = '1';
    const slides = Array.from(track.children);
    // Puntos (siempre) y miniaturas (solo en computador: se arman más abajo desde su <template>)
    let navs = Array.from(root.querySelectorAll('[data-n-gallery-dots] button, [data-n-gallery-thumbs] button'));

    const mark = (index) => {
      navs.forEach((button) => {
        if (Number(button.dataset.index) === index) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    };

    // Fotos en espera (data-n-hold, ver natua.css): la 2.ª se suelta al terminar de cargar la página (ya no compite con la LCP) y todas al primer
    // gesto sobre la galería, al tocar un punto o al cambiar de paquete. Ya sueltas, el loading=lazy del navegador pide solo las cercanas.
    const setHold = (value) => {
      if (value) root.dataset.nHold = value;
      else delete root.dataset.nHold;
    };
    const releaseAll = () => {
      if (root.dataset.nHold) setHold('');
    };
    if (root.dataset.nHold) {
      const releaseNext = () => {
        if (root.dataset.nHold === 'all') setHold('rest');
      };
      const afterLoad = () => (window.requestIdleCallback ? window.requestIdleCallback(releaseNext, { timeout: 2000 }) : window.setTimeout(releaseNext, 300));
      if (document.readyState === 'complete') afterLoad();
      else window.addEventListener('load', afterLoad, { once: true });
      ['pointerdown', 'touchstart', 'keydown', 'focusin'].forEach((type) => root.addEventListener(type, releaseAll, { once: true, passive: true }));
      track.addEventListener('scroll', releaseAll, { once: true, passive: true });
    }

    const go = (index) => {
      const slide = slides[index];
      if (!slide) return;
      releaseAll();
      track.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      mark(index);
    };

    const bindNav = (button) => button.addEventListener('click', () => go(Number(button.dataset.index)));
    navs.forEach(bindNav);

    // Miniaturas: solo se ven con pantalla ≥ 990 px. En el celular no se dibujan (están en un <template>); se arman al pasar a pantalla ancha.
    const thumbsTemplate = root.querySelector('template[data-n-gallery-thumbs-template]');
    if (thumbsTemplate) {
      const wide = window.matchMedia('(min-width: 990px)');
      const mountThumbs = () => {
        if (!wide.matches || !thumbsTemplate.isConnected) return;
        const fragment = thumbsTemplate.content.cloneNode(true);
        const thumbs = Array.from(fragment.querySelectorAll('button'));
        thumbsTemplate.replaceWith(fragment);
        thumbs.forEach(bindNav);
        navs = navs.concat(thumbs);
        mark(Math.round(track.scrollLeft / Math.max(track.clientWidth, 1)));
        if (wide.removeEventListener) wide.removeEventListener('change', mountThumbs);
        else if (wide.removeListener) wide.removeListener(mountThumbs);
      };
      if (wide.addEventListener) wide.addEventListener('change', mountThumbs);
      else if (wide.addListener) wide.addListener(mountThumbs);
      onCleanup(root, () => {
        if (wide.removeEventListener) wide.removeEventListener('change', mountThumbs);
        else if (wide.removeListener) wide.removeListener(mountThumbs);
      });
      mountThumbs();
    }

    let frame = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => mark(Math.round(track.scrollLeft / Math.max(track.clientWidth, 1))));
      },
      { passive: true }
    );

    root.nGo = go;
    root.nSlides = slides;

    const videos = Array.from(track.querySelectorAll('video'));
    if (videos.length && 'IntersectionObserver' in window) {
      const visible = new WeakMap();
      const userPaused = new WeakSet();
      videos.forEach((video) => {
        video.addEventListener('pause', () => visible.get(video) && userPaused.add(video));
        video.addEventListener('play', () => userPaused.delete(video));
      });
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            visible.set(video, entry.isIntersecting);
            if (entry.isIntersecting) {
              if (!userPaused.has(video) && !reduceMotion) video.play().catch(() => {});
            } else {
              video.pause();
            }
          });
        },
        { threshold: 0.6 }
      );
      videos.forEach((video) => {
        if (reduceMotion) video.pause();
        observer.observe(video);
      });
      onCleanup(root, () => observer.disconnect());
    }
  }

  /* ---------- Selector de paquetes ---------- */
  function initTiers(pdp) {
    const inputs = Array.from(pdp.querySelectorAll('.n-tier__input'));
    if (!inputs.length) return;
    const gallery = pdp.querySelector('[data-n-gallery]');
    const cta = pdp.querySelector('[data-n-main-cta]');
    const stickyLabel = pdp.querySelector('[data-n-sticky-label]');
    const stickyPrice = pdp.querySelector('[data-n-sticky-price]');

    const sync = (input, fromUser) => {
      inputs.forEach((item) => item.closest('.n-tier').classList.toggle('is-checked', item === input));
      if (cta && !input.disabled && cta.disabled) {
        cta.disabled = false;
        const ctaText = cta.querySelector('span');
        if (ctaText && cta.dataset.label) ctaText.textContent = cta.dataset.label;
      }
      if (stickyLabel) stickyLabel.textContent = input.dataset.label || '';
      if (stickyPrice) stickyPrice.textContent = input.dataset.price || '';
      if (!fromUser) return;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', input.value);
      window.history.replaceState(window.history.state, '', url.toString());

      const mediaId = input.dataset.mediaId;
      if (mediaId && gallery && gallery.nSlides) {
        const index = gallery.nSlides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (index > -1) gallery.nGo(index);
      }
    };

    // product-form.js de Dawn habilita el primer [name=id]: los paquetes agotados se vuelven a deshabilitar
    inputs.forEach((input) => {
      if (input.dataset.available === 'false') input.disabled = true;
    });
    if (!inputs.some((input) => input.checked && !input.disabled)) {
      const firstAvailable = inputs.find((input) => !input.disabled);
      if (firstAvailable) firstAvailable.checked = true;
    }
    inputs.forEach((input) => input.addEventListener('change', () => sync(input, true)));
    const current = () => inputs.find((input) => input.checked);
    if (current()) sync(current(), false);
    const onPageShow = () => current() && sync(current(), false);
    window.addEventListener('pageshow', onPageShow);
    onCleanup(pdp, () => window.removeEventListener('pageshow', onPageShow));
  }

  /* ---------- Barra fija de compra ----------
     Antes del selector: "Elegir mi ritual" lleva a los paquetes.
     Después del selector: "Quiero sentirme liviana" agrega el paquete elegido al carrito. */
  function initSticky(pdp) {
    const sticky = pdp.querySelector('[data-n-sticky]');
    const buyBox = pdp.querySelector('.n-buy');
    if (!sticky || !buyBox || !('IntersectionObserver' in window)) return;
    const button = sticky.querySelector('[data-n-sticky-buy]');
    const text = sticky.querySelector('[data-n-sticky-text]');
    const form = document.getElementById(button.dataset.form);
    const submit = form ? form.querySelector('[type="submit"]') : null;
    let boxVisible = true;
    let scrolled = window.scrollY > 240;
    let mode = 'scroll';

    sticky.hidden = false;
    sticky.inert = true;
    document.body.classList.add('n-has-sticky');

    const update = () => {
      const passed = buyBox.getBoundingClientRect().bottom < 0;
      // Si no se puede comprar (todo agotado), la barra solo lleva al cuadro de compra
      mode = passed && submit && !submit.disabled ? 'buy' : 'scroll';
      if (text) text.textContent = mode === 'buy' ? button.dataset.labelBuy : button.dataset.labelScroll;
      const visible = !boxVisible && (passed || scrolled);
      sticky.classList.toggle('is-visible', visible);
      sticky.inert = !visible;
      document.body.classList.toggle('n-sticky-on', visible);
    };

    const observer = new IntersectionObserver(([entry]) => {
      boxVisible = entry.isIntersecting;
      update();
    });
    observer.observe(buyBox);

    const onScroll = () => {
      if (!scrolled && window.scrollY > 240) {
        scrolled = true;
        update();
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    onCleanup(pdp, () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      document.body.classList.remove('n-has-sticky', 'n-sticky-on');
    });

    button.addEventListener('click', () => {
      if (mode === 'scroll' || !submit || submit.disabled) {
        buyBox.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        return;
      }
      // Con la oferta al comprar activa: se deja el paquete en el carrito y se abre la ventana (initUpsell), sin pasar por product-form.js
      const upsell = pdp.nUpsell;
      if (upsell && form && upsell.matches(form)) {
        upsell.open(form, button);
        return;
      }
      const spinner = button.querySelector('.loading__spinner');
      const stop = () => {
        button.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
      };
      button.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        const unsubscribers = [];
        const finish = (failed) => {
          stop();
          unsubscribers.forEach((off) => off());
          // El mensaje de error de product-form aparece bajo el botón principal: se lleva a la vista
          if (failed) requestAnimationFrame(() => submit.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' }));
        };
        unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartUpdate, () => finish(false)));
        unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartError, () => finish(true)));
      }
      setTimeout(stop, 6000);
      // Clic al botón principal (no requestSubmit: iOS 15 no lo tiene) → pasa por product-form.js de Dawn
      submit.click();
    });
  }

  /* ---------- Oferta al comprar (ventana de upsell) ----------
     Al tocar comprar Flora la ventana (una tarjeta centrada, snippets/natua-upsell.liquid) se abre AL INSTANTE en una pantalla de
     "desbloqueaste un precio especial" mientras el paquete elegido queda en el carrito; pasa a la oferta (otro producto a precio especial,
     opcional) cuando Flora ya está en el carrito Y la pantalla se vio al menos 1,4 s (0,8 s y sin animación con movimiento reducido).
     El carrito siempre se deja con UNA llamada a /cart/update.js con {id de variante: cantidad}; así volver y comprar otra vez
     nunca duplica ni mezcla paquetes:
       · al abrir: el paquete elegido de Flora = 1 y los otros paquetes de Flora = 0;
       · "Sí": la cantidad elegida de la oferta = 1 y las otras cantidades = 0; luego va al pago;
       · "No, gracias", la ✕, Escape y tocar fuera: toda la oferta = 0; luego va al pago (Flora ya estaba en el pedido).
     Si no se puede dejar Flora en el carrito, la ventana se cierra y el formulario sigue su camino normal (product-form.js).
     Intercepta en la fase de captura (antes que product-form.js) TODO formulario de compra de Flora de la página:
     botón principal, oferta apilada y cierre; la barra fija la abre desde initSticky. Sin JavaScript el formulario funciona como siempre. */
  const upsells = new Set();
  let lastClicked = null;
  // true solo mientras se reenvía un formulario para que pase por product-form.js (plan B cuando no se pudo dejar Flora en el carrito)
  let passThrough = false;

  const isCartAddForm = (form) => {
    if (!form || form.tagName !== 'FORM') return false;
    try {
      return /\/cart\/add\/?$/.test(new URL(form.getAttribute('action') || '', window.location.href).pathname);
    } catch (error) {
      return false;
    }
  };

  // Formatea centavos igual que money_without_trailing_zeros, con el formato de dinero de la tienda (p. ej. ${{amount_no_decimals_with_comma_separator}}).
  // Se verifica contra los precios que ya salieron formateados del servidor; si no coinciden, usa el mismo símbolo y Intl para los miles.
  function createMoney(format, samples) {
    const found = /\{\{\s*(\w+)\s*\}\}/.exec(format || '');
    const styles = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_no_decimals_with_space_separator: [0, ' ', '.'],
      amount_with_space_separator: [2, ' ', ','],
      amount_with_period_and_space_separator: [2, ' ', '.'],
    };
    const style = found && styles[found[1]];
    const fromFormat = (cents) => {
      const [decimals, thousands, decimal] = style;
      const [whole, fraction] = (cents / 100).toFixed(decimals).split('.');
      const number = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
      return format.replace(found[0], decimals === 2 ? number.replace(/[.,]00$/, '') : number);
    };
    if (style && samples.every((sample) => fromFormat(sample.cents) === sample.text)) return fromFormat;

    const sample = samples.length ? samples[0].text : '';
    const prefix = (sample.match(/^\D*/) || [''])[0];
    const suffix = (sample.match(/\D*$/) || [''])[0];
    const grouped = new Intl.NumberFormat(document.documentElement.lang || 'es-CO', { maximumFractionDigits: 0, useGrouping: 'always' });
    return (cents) => prefix + grouped.format(Math.round(cents / 100)) + suffix;
  }

  // Frase con marcadores entre corchetes ("Flora · [paquete] ya está en tu pedido"): cada marcador conocido se cambia por su valor
  // (los de `bold` en negrita) y uno desconocido queda como texto para que se note. El espacio antes de un valor pasa a espacio duro:
  // "Flora $89.900" o "= $304.800" nunca se parten en dos renglones.
  function fillTemplate(element, values, bold = []) {
    if (!element) return;
    const isValue = (token) => /^\[[^\]\s]+\]$/.test(token || '') && Object.prototype.hasOwnProperty.call(values, token.slice(1, -1));
    const tokens = (element.dataset.template || '').split(/(\[[^\]\s]+\])/).filter(Boolean);
    element.textContent = '';
    tokens.forEach((token, index) => {
      if (isValue(token)) {
        const key = token.slice(1, -1);
        if (bold.includes(key)) {
          const strong = document.createElement('strong');
          strong.textContent = values[key];
          element.appendChild(strong);
        } else {
          element.appendChild(document.createTextNode(values[key]));
        }
      } else {
        element.appendChild(document.createTextNode(isValue(tokens[index + 1]) ? token.replace(/ $/, '\u00a0') : token));
      }
    });
  }

  // Deja el carrito con EXACTAMENTE estas cantidades ({id de variante: cantidad}) en una sola llamada. Probado en la tienda: update.js también
  // agrega una variante que todavía no está en el carrito, y una cantidad 0 para una que no está no hace nada.
  async function updateCart(updates) {
    const base = (window.routes && window.routes.cart_update_url) || '/cart/update';
    let response;
    try {
      response = await fetch(`${base}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
    } catch (error) {
      // Sin conexión: el mensaje del navegador sale en inglés; vacío = la ventana muestra su aviso general
      throw new Error('');
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) throw new Error(data.description || data.message || '');
    return data;
  }

  // hidden también para el ícono SVG (un SVG no tiene la propiedad .hidden)
  function toggleHidden(element, hide) {
    if (element) element.toggleAttribute('hidden', hide);
  }

  // Plan B: reenvía el formulario TAL CUAL para que lo maneje product-form.js (agrega, abre el carrito lateral o muestra su aviso)
  function submitNormally(form) {
    const button = form.querySelector('[type="submit"]');
    passThrough = true;
    try {
      // click() en lugar de requestSubmit(): iOS 15 no tiene requestSubmit
      if (button) button.click();
      else if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    } finally {
      passThrough = false;
    }
  }

  // Los montos salen del servidor ya formateados y escapados (un símbolo como &euro; llega como texto): se decodifican igual que los que pinta el navegador
  const decodeHtml = (html) => {
    const box = document.createElement('textarea');
    box.innerHTML = html || '';
    return box.value;
  };

  // Arranque liviano: solo lee los paquetes de Flora (datos) y se registra para interceptar la compra. La tarjeta de la ventana vive en un
  // <template> (snippets/natua-upsell.liquid) y se arma con mountUpsell la primera vez que hace falta: en cuanto la clienta toca el área de
  // compra (ya está lista cuando envía el formulario) o, a más tardar, al abrirla. Mientras tanto no suma elementos a la página.
  function initUpsell(pdp) {
    const root = pdp.querySelector('[data-n-upsell]');
    const template = root ? root.querySelector('template[data-n-up-template]') : null;
    // Sin fetch (navegador muy antiguo) no se intercepta nada: el formulario funciona como siempre
    if (!root || !template || root.dataset.nReady || typeof window.fetch !== 'function') return;
    const packs = new Map(
      Array.from(root.querySelectorAll('[data-n-up-flora] li')).map((item) => [
        item.dataset.id,
        { index: Number(item.dataset.index), label: item.dataset.label, price: Number(item.dataset.price), money: decodeHtml(item.dataset.money) },
      ])
    );
    // Sin los paquetes no se intercepta nada: la compra sigue como siempre
    if (!packs.size) return;
    root.dataset.nReady = '1';

    let dialog = null;
    let broken = false;
    const mount = () => {
      if (!dialog && !broken) {
        dialog = mountUpsell(pdp, root, template, packs);
        broken = !dialog;
      }
      return dialog;
    };

    const upsell = {
      // ¿Este formulario agrega un paquete de Flora? (los demás formularios de compra no se tocan)
      matches(form) {
        if (!isCartAddForm(form)) return false;
        const id = new FormData(form).get('id');
        return id !== null && packs.has(String(id));
      },
      open(form, opener) {
        const real = mount();
        // Si la ventana no se pudo armar, la compra sigue por product-form.js como si no existiera
        if (!real) {
          submitNormally(form);
          return Promise.resolve();
        }
        return real.open(form, opener);
      },
    };
    pdp.nUpsell = upsell;
    upsells.add(upsell);

    // La ventana se arma (y su foto se pide) en cuanto la clienta toca el área de compra, para que ya esté lista cuando se abra
    const warm = (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('.n-buy, .n-sticky, .n-cta')) return;
      document.removeEventListener('pointerdown', warm);
      document.removeEventListener('focusin', warm);
      mount();
    };
    document.addEventListener('pointerdown', warm, { passive: true });
    document.addEventListener('focusin', warm);

    onCleanup(pdp, () => {
      upsells.delete(upsell);
      document.removeEventListener('pointerdown', warm);
      document.removeEventListener('focusin', warm);
      if (dialog) dialog.destroy();
      delete pdp.nUpsell;
    });
  }

  // Arma la ventana: clona la tarjeta del <template>, toma sus piezas y conecta todo. Devuelve { open, destroy } (o null si falta algo).
  function mountUpsell(pdp, root, template, packs) {
    // Las fotos van con loading=eager: la ventana está oculta (una imagen lazy oculta nunca se pediría) y así ya están cuando se abre
    const content = template.content.cloneNode(true);
    content.querySelectorAll('img').forEach((photo) => {
      photo.loading = 'eager';
    });
    root.appendChild(content);
    template.remove();

    const panel = root.querySelector('[data-n-up-panel]');
    const unlockBox = root.querySelector('[data-n-up-unlock]');
    const view = root.querySelector('[data-n-up-view]');
    const scroller = root.querySelector('[data-n-up-scroll]');
    const backdrop = root.querySelector('[data-n-up-backdrop]');
    const closeButton = root.querySelector('[data-n-up-close]');
    const yes = root.querySelector('[data-n-up-yes]');
    const no = root.querySelector('[data-n-up-no]');
    const errorBox = root.querySelector('[data-n-up-error]');
    const status = root.querySelector('[data-n-up-status]');
    const radios = Array.from(root.querySelectorAll('.n-up__radio'));
    const decode = decodeHtml;
    // Todas las variantes de la oferta (también las agotadas): al rechazar, todas quedan en 0
    const resetIds = (root.dataset.resetIds || '').split(',').filter(Boolean);
    const out = {
      added: root.querySelector('[data-n-up-added]'),
      chosen: root.querySelector('[data-n-up-chosen]'),
      summary: root.querySelector('[data-n-up-summary]'),
      yesText: root.querySelector('[data-n-up-yes-text]'),
      noText: root.querySelector('[data-n-up-no-text]'),
      normal: root.querySelector('[data-n-up-normal]'),
      compare: root.querySelector('[data-n-up-compare]'),
      special: root.querySelector('[data-n-up-special]'),
      off: root.querySelector('[data-n-up-off]'),
      arrow: root.querySelector('.n-up__arrow'),
    };
    // Sin datos completos no se intercepta nada: la compra sigue como siempre
    if (!panel || !unlockBox || !view || !scroller || !backdrop || !yes || !no || !closeButton || !errorBox || !out.yesText || !out.noText || !radios.length) return null;

    // CSS de la ventana (assets/natua-upsell.css): se pide sin bloquear la pintura; si todavía no llegó (red lenta) se espera un instante
    // antes de mostrar la ventana, para que nunca se vea sin estilos. Si no llega (bloqueado o sin red) la ventana no se abre: la compra
    // sigue por product-form.js, como si la oferta estuviera apagada (mejor sin oferta que una ventana invisible que frena el pedido).
    const cssLink = document.querySelector('link[data-n-up-css]');
    // "Cargó" = no marcó error (onerror del enlace) y su hoja ya tiene reglas (un archivo que falló puede dejar una hoja vacía)
    const sheetOk = () => {
      try {
        return Boolean(cssLink.sheet) && cssLink.sheet.cssRules.length > 0;
      } catch (error) {
        return Boolean(cssLink.sheet); // hoja de otro dominio: no se pueden leer las reglas
      }
    };
    const styleReady = () => !cssLink || (cssLink.dataset.nFail !== '1' && sheetOk());
    const waitStyle = () =>
      new Promise((resolve) => {
        const done = () => {
          if (cssLink.dataset.nFail !== '1') cssLink.media = 'all';
          resolve(styleReady());
        };
        if (cssLink.dataset.nFail === '1') return resolve(false); // ya falló: sin esperar
        cssLink.addEventListener('load', done, { once: true });
        cssLink.addEventListener('error', done, { once: true });
        window.setTimeout(done, 2500);
        cssLink.media = 'all'; // ya no hace falta esperar al final de la carga de la página para aplicarla
      });

    const samples = radios
      .map((radio) => ({ cents: Number(radio.dataset.price), text: decode(radio.dataset.money) }))
      .concat(Array.from(packs.values()).map((pack) => ({ cents: pack.price, text: pack.money })));
    const money = createMoney(decode(root.dataset.moneyFormat), samples);
    // stage: '' (cerrada) · 'unlock' (pantalla "desbloqueaste", Flora yendo al carrito) · 'offer' (la oferta). run: invalida lo que quedó esperando.
    const state = { open: false, stage: '', busy: false, waiting: false, floraId: '', opener: null, openedAt: 0, timer: 0, run: 0 };
    // La pantalla "desbloqueaste" se ve al menos este tiempo desde el toque (con movimiento reducido, menos y sin animación)
    const UNLOCK_MS = reduceMotion ? 800 : 1400;
    // Un segundo toque del mismo dedo (doble toque en "comprar") no debe aceptar, rechazar ni cerrar nada
    const settled = () => performance.now() - state.openedAt > 450;

    // Lo que eligió la clienta: la frase sale del ajuste ("Flora · [paquete] ya está en tu pedido") con el paquete del formulario
    // (nombre y precio en negrita). Se actualiza cada vez que se abre.
    const showPack = (pack) => {
      fillTemplate(out.chosen, { paquete: pack.label, precio: pack.money }, ['paquete', 'precio']);
    };

    // Cantidad elegida (tarjeta marcada) y todo lo que depende de ella, en vivo: precio normal → precio con Flora y % de descuento,
    // línea "Tu pedido: Flora + Reset = total" (sumado en centavos) y el precio dentro de los dos botones
    const render = () => {
      const pack = packs.get(state.floraId);
      const choice = radios.find((radio) => radio.checked);
      if (!pack || !choice) return;
      radios.forEach((radio) => radio.closest('.n-up__opt').classList.toggle('is-checked', radio === choice));
      const offer = {
        cents: Number(choice.dataset.price),
        money: decode(choice.dataset.money),
        compare: decode(choice.dataset.compareMoney),
        off: Number(choice.dataset.off) || 0,
      };
      if (out.special) out.special.textContent = offer.money;
      if (out.compare) out.compare.textContent = offer.compare;
      // Sin precio de comparación no hay precio tachado, flecha ni descuento
      const showOff = Boolean(offer.compare) && offer.off >= 1 && Boolean(out.off && out.off.dataset.template);
      toggleHidden(out.normal, !offer.compare);
      toggleHidden(out.arrow, !offer.compare);
      toggleHidden(out.off, !showOff);
      if (showOff) fillTemplate(out.off, { porcentaje: String(offer.off) });
      fillTemplate(out.summary, { flora: pack.money, reset: offer.money, total: money(pack.price + offer.cents) }, ['total']);
      fillTemplate(out.yesText, { precio: offer.money });
      fillTemplate(out.noText, { precio: pack.money });
    };
    radios.forEach((radio) => radio.addEventListener('change', render));

    // Bloqueo del scroll de la página (body fijo: también funciona en iOS antiguo) y regreso exacto al cerrar
    let lockY = 0;
    const lock = () => {
      lockY = window.scrollY;
      const gap = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.top = `-${lockY}px`;
      if (gap > 0) document.body.style.paddingRight = `${gap}px`;
      document.documentElement.classList.add('n-lock');
      document.body.classList.add('n-lock');
    };
    const unlock = () => {
      const html = document.documentElement;
      const behavior = html.style.scrollBehavior;
      html.classList.remove('n-lock');
      document.body.classList.remove('n-lock');
      document.body.style.top = '';
      document.body.style.paddingRight = '';
      html.style.scrollBehavior = 'auto';
      window.scrollTo(0, lockY);
      html.style.scrollBehavior = behavior;
    };

    const setError = (message) => {
      errorBox.textContent = message || '';
      errorBox.hidden = !message;
    };

    // Mientras trabaja (después de elegir): todo deshabilitado y el botón que se tocó dice "Un momento…" (data-busy="yes" o "no"; el CSS
    // cambia el texto). La ✕, Escape y tocar fuera cuentan como "no".
    const setBusy = (busy, source = '') => {
      state.busy = busy;
      root.classList.toggle('is-busy', busy);
      if (busy) root.dataset.busy = source;
      else delete root.dataset.busy;
      panel.setAttribute('aria-busy', busy ? 'true' : 'false');
      [yes, no, closeButton].concat(radios).forEach((element) => {
        element.disabled = busy;
      });
      if (status) status.textContent = busy ? root.dataset.loading || '' : '';
      // Un botón deshabilitado pierde el foco: se queda dentro de la ventana
      if (busy) panel.focus({ preventScroll: true });
    };

    // Cambia de pantalla dentro de la misma tarjeta (cada una con su nombre para el lector de pantalla)
    const setStage = (stage) => {
      state.stage = stage;
      if (stage) root.dataset.stage = stage;
      else delete root.dataset.stage;
      unlockBox.hidden = stage !== 'unlock';
      view.hidden = stage !== 'offer';
      // Sin aria-busy en la pantalla "desbloqueaste": un aria-busy="true" haría que el lector de pantalla NO anuncie lo que cambia ahí dentro
      panel.setAttribute('aria-labelledby', (stage === 'offer' ? panel.dataset.labelOffer : panel.dataset.labelUnlock) || '');
      if (stage === 'offer' && panel.dataset.descOffer) panel.setAttribute('aria-describedby', panel.dataset.descOffer);
      else panel.removeAttribute('aria-describedby');
    };

    // Trampa de foco: Tab da la vuelta dentro de la ventana; Escape hace lo mismo que "No, gracias" (y no hace nada en la pantalla "desbloqueaste")
    const focusables = () =>
      Array.from(panel.querySelectorAll('button, input, a[href], [tabindex]'))
        .filter((el) => !el.disabled && el.tabIndex >= 0 && !el.closest('[hidden]'));

    const onKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        finish(false);
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!panel.contains(active) || (event.shiftKey && (active === first || active === panel))) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const onFocusIn = (event) => {
      if (state.open && !panel.contains(event.target)) panel.focus({ preventScroll: true });
    };

    // Cierra la ventana (sin devolver el foco: quien la llama decide)
    const hideDialog = () => {
      state.run += 1;
      state.open = false;
      state.stage = ''; // la pantalla que se veía queda tal cual mientras la tarjeta se desvanece
      root.classList.remove('is-open');
      document.removeEventListener('keydown', onKeydown, true);
      document.removeEventListener('focusin', onFocusIn);
      unlock();
      window.clearTimeout(state.timer);
      if (reduceMotion) root.hidden = true;
      else state.timer = window.setTimeout(() => (root.hidden = true), 300);
    };

    // La ventana ya no se cierra sola hacia la página de producto (todo va al pago); solo se cierra al volver con "atrás" desde el pago
    const closeDialog = () => {
      if (!state.open || state.busy) return;
      hideDialog();
      const target = state.opener && document.contains(state.opener) ? state.opener : pdp.querySelector('[data-n-main-cta]');
      state.opener = null;
      if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
    };

    // Pantalla 2 · la oferta: llena el aviso, la cantidad inicial y los precios, y cambia de pantalla dentro de la misma tarjeta
    const showOffer = (pack) => {
      showPack(pack);
      // Cantidad inicial = mismo número que el paquete de Flora (1→1, 2→2, 3→3); si no está disponible, la primera disponible
      const initial = radios.find((radio) => Number(radio.dataset.index) === pack.index) || radios[0];
      initial.checked = true;
      setError('');
      render();
      setStage('offer');
      scroller.scrollTop = 0;
      state.openedAt = performance.now(); // el toque doble de "comprar" no debe caer sobre los botones recién aparecidos
      // El lector de pantalla oye el cambio: título de la oferta y aviso de que Flora ya está en el pedido
      if (status) status.textContent = [out.chosen && out.chosen.textContent, panel.querySelector('.n-up__title').textContent].filter(Boolean).join('. ');
      panel.focus({ preventScroll: true });
    };

    // Al tocar comprar la ventana se abre AL INSTANTE en la pantalla "desbloqueaste" mientras Flora se deja en el carrito (una sola llamada:
    // el paquete elegido en 1 y los otros paquetes en 0). La pantalla dura al menos UNLOCK_MS y solo entonces pasa a la oferta.
    // Si no se pudo dejar Flora en el carrito, se cierra y el formulario sigue su camino normal (product-form.js muestra su propio aviso).
    const openDialog = async (form, opener) => {
      const floraId = String(new FormData(form).get('id'));
      const pack = packs.get(floraId);
      if (state.open || state.busy || state.waiting || !pack) return;
      // Si el CSS de la ventana todavía no llegó (red lenta), se espera un instante: nunca se muestra sin estilos
      if (!styleReady()) {
        state.waiting = true;
        const styled = await waitStyle();
        state.waiting = false;
        if (!styled) {
          submitNormally(form);
          return;
        }
      }
      const run = state.run + 1;
      state.run = run;
      window.clearTimeout(state.timer);
      state.floraId = floraId;
      state.opener = opener;
      // La línea "✓ Agregamos Flora" ya está escrita pero oculta: se muestra recién cuando Flora quedó en el carrito
      if (out.added) {
        out.added.classList.remove('is-in');
        fillTemplate(out.added, { paquete: pack.label, precio: pack.money }, ['paquete', 'precio']);
      }
      if (status) status.textContent = '';
      setError('');
      setStage('unlock');
      lock();
      root.hidden = false;
      void root.offsetWidth; // fija los estilos de salida antes de la animación de entrada
      root.classList.add('is-open');
      state.open = true;
      state.openedAt = performance.now();
      document.addEventListener('keydown', onKeydown, true);
      document.addEventListener('focusin', onFocusIn);
      panel.focus({ preventScroll: true });

      const updates = {};
      Array.from(packs.keys()).forEach((id) => {
        updates[id] = 0;
      });
      updates[floraId] = 1;
      let added = false;
      try {
        await updateCart(updates);
        added = true;
      } catch (error) {
        added = false;
      }
      // La ventana se cerró mientras tanto (volvió con "atrás"): no se hace nada más
      if (run !== state.run || !state.open) return;
      if (!added) {
        hideDialog();
        state.opener = null;
        submitNormally(form);
        return;
      }
      if (out.added) out.added.classList.add('is-in');
      const rest = UNLOCK_MS - (performance.now() - state.openedAt);
      if (rest > 0) await new Promise((resolve) => window.setTimeout(resolve, rest));
      if (run !== state.run || !state.open) return;
      showOffer(pack);
    };

    // Paso final · Flora ya está en el carrito: con "Sí" la cantidad elegida de la oferta queda en 1 y las demás en 0; si no, toda la oferta
    // queda en 0. Luego va directo al pago. Si falla, avisa dentro de la ventana y vuelve a habilitar todo para reintentar.
    // En la pantalla "desbloqueaste" no hace nada (todavía no se ve ninguna opción).
    async function finish(withOffer) {
      if (state.busy || !state.open || state.stage !== 'offer' || !settled()) return;
      const choice = radios.find((radio) => radio.checked);
      if (withOffer && !choice) return;
      const updates = {};
      resetIds.forEach((id) => {
        updates[id] = 0;
      });
      if (withOffer) updates[choice.value] = 1;
      setError('');
      setBusy(true, withOffer ? 'yes' : 'no');
      let failure = '';
      try {
        await updateCart(updates);
        // La página recuerda dónde estaba el scroll por si la clienta vuelve con "atrás" (la ventana sigue tapando hasta salir)
        unlock();
        window.location.href = '/checkout';
        return;
      } catch (error) {
        failure = error && error.message ? error.message : '';
      }
      // El foco se queda en la ventana: el lector de pantalla lee el aviso (role="alert") sin que otro anuncio lo corte
      setBusy(false);
      setError(failure || root.dataset.error);
    }

    yes.addEventListener('click', () => finish(true));
    // "No, gracias", la ✕ y tocar fuera son lo mismo: toda la oferta fuera del carrito, directo al pago solo con Flora
    [no, closeButton, backdrop].forEach((element) => element.addEventListener('click', () => finish(false)));

    // Volver con "atrás" desde el pago (la página se restaura tal cual): la ventana no debe quedar abierta "trabajando"
    const onPageShow = (event) => {
      if (!event.persisted) return;
      state.run += 1;
      setBusy(false);
      closeDialog();
    };
    window.addEventListener('pageshow', onPageShow);

    return {
      open: openDialog,
      destroy() {
        state.run += 1;
        if (state.open) {
          document.removeEventListener('keydown', onKeydown, true);
          document.removeEventListener('focusin', onFocusIn);
          unlock();
        }
        window.removeEventListener('pageshow', onPageShow);
      },
    };
  }

  // Quién tocó último (para devolverle el foco al cerrar cuando el navegador no informa el botón que envió el formulario)
  document.addEventListener(
    'click',
    (event) => {
      lastClicked = event.target instanceof Element ? event.target.closest('button, a, input, summary') : null;
    },
    true
  );

  // Fase de captura en document: corre antes que el submit de product-form.js (que está en el formulario)
  document.addEventListener(
    'submit',
    (event) => {
      if (passThrough || !upsells.size) return;
      for (const upsell of upsells) {
        if (!upsell.matches(event.target)) continue;
        event.preventDefault();
        event.stopImmediatePropagation();
        upsell.open(event.target, event.submitter || lastClicked || document.activeElement);
        return;
      }
    },
    true
  );

  /* ---------- Fecha estimada de entrega (días hábiles, sin festivos) ---------- */
  const DAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
  const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

  const isoDate = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  function addBusinessDays(count, holidays) {
    const date = new Date();
    let added = 0;
    while (added < count) {
      date.setDate(date.getDate() + 1);
      const weekday = date.getDay();
      if (weekday !== 0 && weekday !== 6 && !holidays.has(isoDate(date))) added += 1;
    }
    return date;
  }

  function initDelivery(el) {
    const output = el.querySelector('[data-n-delivery-text]');
    const template = el.dataset.template || '';
    if (!output || template.indexOf('[min]') === -1) return;
    const min = parseInt(el.dataset.min, 10) || 2;
    const max = Math.max(parseInt(el.dataset.max, 10) || 5, min);
    const holidays = new Set((el.dataset.holidays || '').match(/\d{4}-\d{2}-\d{2}/g) || []);
    const from = addBusinessDays(min, holidays);
    const to = addBusinessDays(max, holidays);
    const fromText =
      from.getMonth() === to.getMonth()
        ? `${DAYS[from.getDay()]} ${from.getDate()}`
        : `${DAYS[from.getDay()]} ${from.getDate()} de ${MONTHS[from.getMonth()]}`;
    const toText = `${DAYS[to.getDay()]} ${to.getDate()} de ${MONTHS[to.getMonth()]}`;

    output.textContent = '';
    template.split(/(\[min\]|\[max\])/).forEach((part) => {
      if (part === '[min]' || part === '[max]') {
        const strong = document.createElement('strong');
        strong.textContent = part === '[min]' ? fromText : toText;
        output.append(strong);
      } else if (part) {
        output.append(part);
      }
    });
  }

  /* ---------- Plato interactivo (Fórmula Plato Completo) ---------- */
  function initPlate(root) {
    const foods = Array.from(root.querySelectorAll('.n-food'));
    const count = root.querySelector('[data-n-count]');
    const countLabel = root.querySelector('[data-n-count-label]');
    const list = root.querySelector('[data-n-enz-list]');
    const empty = root.querySelector('[data-n-empty]');
    const slots = new Map(Array.from(root.querySelectorAll('.n-plate__slot')).map((slot) => [slot.dataset.slot, slot]));
    if (!foods.length || !count || !list) return;

    const render = () => {
      const enzymes = new Map();
      foods.forEach((food) => {
        const on = food.getAttribute('aria-pressed') === 'true';
        const slot = slots.get(food.dataset.slot);
        if (slot) slot.classList.toggle('is-on', on);
        if (on && !enzymes.has(food.dataset.enzyme)) enzymes.set(food.dataset.enzyme, food.dataset.desc);
      });
      count.textContent = String(enzymes.size);
      if (countLabel) countLabel.textContent = enzymes.size === 1 ? countLabel.dataset.one : countLabel.dataset.many;
      list.textContent = '';
      enzymes.forEach((desc, name) => {
        const item = document.createElement('li');
        item.className = 'n-enz';
        const strong = document.createElement('strong');
        strong.textContent = name;
        const text = document.createElement('span');
        text.textContent = ` ${desc}`;
        item.append(strong, text);
        list.append(item);
      });
      if (empty) empty.hidden = enzymes.size > 0;
    };

    foods.forEach((food) =>
      food.addEventListener('click', () => {
        food.setAttribute('aria-pressed', food.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
        render();
      })
    );
  }

  /* ---------- Carrito lateral: cambiar a 3 frascos con un clic ---------- */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-n-upgrade]');
    if (!button || button.disabled || typeof fetchConfig !== 'function' || !window.routes) return;
    const drawer = document.querySelector('cart-drawer');
    const spinner = button.querySelector('.loading__spinner');
    const errors = document.getElementById('CartDrawer-CartErrors');
    const busy = (on) => {
      button.disabled = on;
      button.classList.toggle('loading', on);
      if (spinner) spinner.classList.toggle('hidden', !on);
    };
    busy(true);
    if (errors) errors.textContent = '';
    try {
      const canRender = drawer && typeof drawer.renderContents === 'function';
      const sections = canRender ? drawer.getSectionsToRender().map((section) => section.id) : [];
      const config = fetchConfig();
      config.body = JSON.stringify({
        updates: { [button.dataset.from]: 0, [button.dataset.to]: 1 },
        sections,
        sections_url: window.location.pathname,
      });
      const response = await fetch(window.routes.cart_update_url, config);
      const state = await response.json();
      if (state.status) throw new Error(state.description || state.message || '');

      // En la página /cart el resto de la página (totales) no se refresca solo: se recarga.
      if (!canRender || document.getElementById('main-cart-footer')) {
        window.location.reload();
        return;
      }

      drawer.renderContents(state);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'natua-upgrade', cartData: state });
      }
      // El cajón ya estaba abierto: se reconstruye la trampa de foco (Escape y Tab vuelven a funcionar)
      if (typeof trapFocus === 'function') {
        const container = document.getElementById('CartDrawer') || drawer;
        trapFocus(container, drawer.querySelector('.cart-item__name') || drawer.querySelector('.drawer__close'));
      }
    } catch (error) {
      console.error(error);
      if (errors) errors.textContent = error.message || (window.cartStrings && window.cartStrings.error) || '';
      busy(false);
    }
  });

  /* ---------- La lista de comidas se "tacha" al entrar en pantalla (una sola vez) ---------- */
  function initCrossed(root) {
    if (reduceMotion || !('IntersectionObserver' in window) || root.dataset.nReady) return;
    root.dataset.nReady = '1';
    root.classList.add('js-n-anim');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.35 }
    );
    observer.observe(root);
    onCleanup(root, () => observer.disconnect());
  }

  /* ---------- Advertorial: barra fija mientras no se ve ningún recuadro de compra ---------- */
  function initArticleSticky(article) {
    const sticky = article.querySelector('[data-n-art-sticky]');
    if (!sticky || sticky.dataset.nReady || !('IntersectionObserver' in window)) return;
    sticky.dataset.nReady = '1';
    // Recuadros de compra de TODA la página: el artículo puede seguir en otras secciones (plato, continuación)
    const ctas = Array.from(document.querySelectorAll('.n-art__cta, .n-art__offer, .n-plate__cta'));
    const visible = new Set();
    let past = false;
    sticky.hidden = false;
    sticky.inert = true;
    document.body.classList.add('n-has-sticky');
    const update = () => {
      const show = past && visible.size === 0;
      sticky.classList.toggle('is-visible', show);
      sticky.inert = !show;
      document.body.classList.toggle('n-sticky-on', show);
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => (entry.isIntersecting ? visible.add(entry.target) : visible.delete(entry.target)));
      update();
    });
    ctas.forEach((cta) => observer.observe(cta));
    const onScroll = () => {
      const next = window.scrollY > 600;
      if (next !== past) {
        past = next;
        update();
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    onCleanup(article, () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      document.body.classList.remove('n-has-sticky', 'n-sticky-on');
    });
  }

  /* ---------- Fotos que no se piden mientras la página carga (snippets/natua-late-image) ----------
     Salen con una imagen vacía de su proporción y la dirección real en data-src / data-srcset. Se les pone al terminar de cargar la página
     (en reposo) o antes si la clienta baja por la página (scroll): así no compiten con la imagen principal (LCP). Tocar "comprar" NO las
     suelta (no deben competir con la llamada del carrito). Ya puestas, el loading="lazy" del navegador pide solo las cercanas. */
  function initLate(scope) {
    const images = Array.from(scope.querySelectorAll('img[data-n-late]'));
    if (!images.length) return;
    let done = false;
    const release = () => {
      if (done) return;
      done = true;
      window.removeEventListener('scroll', release);
      images.forEach((image) => {
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
        if (image.dataset.src) image.src = image.dataset.src;
        image.removeAttribute('data-n-late');
      });
    };
    window.addEventListener('scroll', release, { passive: true });
    const idle = () => (window.requestIdleCallback ? window.requestIdleCallback(release, { timeout: 3000 }) : window.setTimeout(release, 300));
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  /* ---------- Arranque (y re-arranque en el editor de temas) ---------- */
  function boot(scope) {
    scope.querySelectorAll('[data-n-gallery]').forEach(initGallery);
    scope.querySelectorAll('[data-n-pdp]').forEach((pdp) => {
      initTiers(pdp);
      initUpsell(pdp);
      initSticky(pdp);
    });
    scope.querySelectorAll('[data-n-delivery]').forEach(initDelivery);
    scope.querySelectorAll('[data-n-plate]').forEach(initPlate);
    scope.querySelectorAll('[data-n-crossed]').forEach(initCrossed);
    scope.querySelectorAll('.n-art').forEach(initArticleSticky);
    initLate(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  // Editor de temas: al seleccionar un desplegable o una pregunta, se abre
  document.addEventListener('shopify:block:select', (event) => {
    const details = event.target.matches('details') ? event.target : event.target.closest('details');
    if (details) details.open = true;
  });
})();
